import { Map } from 'immutable';

import { setDefaultValuesToCombinations, updateCombinationsWithPreferredValues } from './combinations';
import { getSize } from '../../utils/iterable/getSize';

import { ALL, COMBINATIONS, INFLUENCE, INFLUENCE_ATTRIBUTES, VARIANTS } from '../../constants/variations';
import { ETSY, SHOPIFY } from '../../constants/channels';
import { EMPTY } from '../../constants';
import ATTRIBUTE from '../../constants/attributes';

export function updateInfluence({ channel, variations }) {
  function reduceToTrackQuantity(result, combination) {
    return result && combination.get(ATTRIBUTE.TRACK_QUANTITY);
  }

  switch (channel) {
    case SHOPIFY: {
      const trackQuantity = variations.get(COMBINATIONS).reduce(reduceToTrackQuantity, true);
      return variations.setIn([INFLUENCE, ATTRIBUTE.TRACK_QUANTITY], trackQuantity);
    }

    default: {
      return variations;
    }
  }
}

export function isOnlyInfluence({ channel, attribute, influence }) {
  switch (channel) {
    case ETSY: {
      switch (attribute) {
        case ATTRIBUTE.PRICE: {
          return getSize(influence.get(ATTRIBUTE.QUANTITY)) === 0 && getSize(influence.get(ATTRIBUTE.SKU)) === 0;
        }

        case ATTRIBUTE.QUANTITY: {
          return getSize(influence.get(ATTRIBUTE.PRICE)) === 0 && getSize(influence.get(ATTRIBUTE.SKU)) === 0;
        }

        case ATTRIBUTE.SKU: {
          return getSize(influence.get(ATTRIBUTE.PRICE)) === 0 && getSize(influence.get(ATTRIBUTE.QUANTITY)) === 0;
        }

        default: {
          return false;
        }
      }
    }

    default: {
      return undefined;
    }
  }
}

export function setAllInfluenced(variations) {
  const variants = variations.get(VARIANTS);
  const variantsSize = getSize(variants);

  function reduceInfluence(result, influence) {
    return result || getSize(influence) === variantsSize;
  }

  const all = variantsSize > 0
    ? variations.get(INFLUENCE).delete(ALL).reduce(reduceInfluence, false)
    : false;

  return variations.setIn([INFLUENCE, ALL], all);
}

export function changeInfluence({ channel, attribute, inventory, variantIndex, variations }) {
  function reduceVariantsToInfluence(influence, variant, index) {
    return influence.set(index, true);
  }

  function setAllOrNothing(influence) {
    const all = variations.get(VARIANTS).reduce(reduceVariantsToInfluence, EMPTY.MAP);

    function reduceAttributes(result, attribute) {
      return getSize(influence.get(attribute)) > 0
        ? result.set(attribute, all)
        : result.set(attribute, EMPTY.MAP);
    }

    return INFLUENCE_ATTRIBUTES[channel].reduce(reduceAttributes, Map({ [ALL]: getSize(all) > 0 }));
  }

  switch (channel) {
    case ETSY: {
      const influence = variations.get(INFLUENCE);
      const variants = variations.get(VARIANTS);
      const variantsSize = getSize(variants);

      if (variantsSize === 1) {
        return setDefaultValuesToCombinations({
          attribute,
          inventory,
          variations: setAllInfluenced(
            influence.getIn([attribute, variantIndex])
              ? variations.deleteIn([INFLUENCE, attribute, variantIndex])
              : variations.setIn([INFLUENCE, attribute, variantIndex], true)
          ),
        });
      } else if (influence.get(ALL)) {
        if (getSize(influence.get(attribute)) > 0) {
          if (isOnlyInfluence({ channel, attribute, influence })) {
            return updateCombinationsWithPreferredValues({
              attribute,
              variantIndex: Number(!variantIndex),
              variations: setAllInfluenced(variations.set(INFLUENCE, influence.deleteIn([attribute, variantIndex]))),
            });
          } else {
            return setDefaultValuesToCombinations({
              attribute,
              inventory,
              variations: setAllInfluenced(variations.set(INFLUENCE, influence.set(attribute, EMPTY.MAP))),
            });
          }
        } else {
          return setDefaultValuesToCombinations({
            attribute,
            inventory,
            variations: setAllInfluenced(
              variations.set(INFLUENCE, influence.set(attribute, variants.reduce(reduceVariantsToInfluence, EMPTY.MAP)))
            ),
          });
        }
      } else if (influence.getIn([attribute, variantIndex])) {
        return setDefaultValuesToCombinations({
          attribute,
          inventory,
          variations: variations.deleteIn([INFLUENCE, attribute, variantIndex]),
        });
      } else if (getSize(influence.get(attribute))) {
        return variations.set(INFLUENCE, setAllOrNothing(influence));
      } else {
        return setDefaultValuesToCombinations({
          attribute,
          inventory,
          variations: variations.setIn([INFLUENCE, attribute, variantIndex], true),
        });
      }
    }

    default: {
      return variations;
    }
  }
}
